import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  KTSVG,
  SimpleAlert,
  userHasRole,
  toAbsoluteUrl,
} from '../../../../../_mexablock/helpers';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { NumericFormat } from 'react-number-format';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { approvisionamiento } from '../../../../service/operacion/Cuentas';
import Select from 'react-select';
import { getSelectEmpresas } from '../../../../service/empresas/Empresas';
import { ApprovisionValues } from '../models/CuentaModel';
import { UserModel } from '../../../../modules/auth/models/UserModel';
import { RootState } from '../../../../../setup';
import { getAllSelect } from '../../../../service/distribuidores/Distribuidores';
import { styleSelect } from '../../../../../_mexablock/helpers/StyleSelect';
import { getSelectCatalog } from '../../../../service/catalog/Catalogs';
import * as auth from '../../../../modules/auth/redux/AuthRedux';
import { getUserByToken } from '../../../../modules/auth/redux/AuthCRUD';

interface SelectOption {
  label: string;
  value: string;
  id?: string;
  account?: {
    typeCommission: { code: string };
    commission_percent: number;
  };
  type?: { code: string };
  saldo?: number;
  tipo?: string;
  partner?: { code: string };
  active?: boolean;
}

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
};

const AprovisionamientoModal: React.FC<Props> = ({ show, handleClose }) => {
  const userSesion: UserModel = useSelector<RootState>(
    ({ auth }) => auth.user,
    shallowEqual,
  ) as UserModel;
  const { roles: userRoles } = userSesion;
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  // State
  const [loading, setLoading] = useState(false);
  const [radioSelection, setRadioSelection] = useState('');
  const [empresas, setEmpresas] = useState<SelectOption[]>([]);
  const [distribuidores, setDistribuidores] = useState<SelectOption[]>([]);
  const [tiposPago, setTiposPago] = useState<SelectOption[]>([]);
  const [commission, setCommission] = useState(0);
  const [typeCommission, setTypeCommission] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [isConsignment, setIsConsignment] = useState(false);
  const [saldoActual, setSaldoActual] = useState<number>(0);
  const MAX_SIZE = 5 * 1024 * 1024;
  // Derived state
  const isAdmin = useMemo(
    () => userHasRole(['ADMIN_MEXABLOCK'], userRoles),
    [userRoles],
  );

  const empresasOptions = useMemo(
    () => empresas.filter((emp) => emp.active === true && emp.type?.code === radioSelection),
    [empresas, radioSelection],
  );

  const distribuidoresAdminOptions = useMemo(
    () => distribuidores.filter((dist) =>
      // dist.partner?.code === 'MXB' && 
      dist.active === true),
    [distribuidores],
  );

  const typeClient = [
    {
      label: 'Empresa',
      value: 'empresa',
    },
    {
      label: 'Socio',
      value: 'socio',
    },
    {
      label: 'Distribuidor',
      value: 'distribuidor',
    },
  ];

  const typeAprov = [
    {
      label: 'Venta directa',
      value: 'direct',
    },
    {
      label: 'Consignación',
      value: 'consig',
    },
  ];

  const initialValues: ApprovisionValues = {
    empresa: null,
    efectivo: 0,
    virtual: 0,
    comments: '',
    file: null,
    tipoPago: null,
    cliente: '',
    typeAprov: 'direct',
    commission: 0,
  };

  // Validation schema
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        virtual: Yup.number()
          .min(1, 'El saldo debe ser mayor a 0')
          .required('El saldo virtual es requerido'),
        empresa: Yup.mixed().required('Seleccione una empresa o distribuidor'),

        ...(isAdmin && {
          cliente: Yup.mixed().required('Seleccione el tipo de cliente'),
          typeAprov: Yup.mixed().required(
            'Seleccione el tipo de aprovisionamiento',
          ),
        }),
        tipoPago: Yup.mixed().required('Seleccione el tipo de pago'),
        file: Yup.mixed()
          .nullable()
          .test('fileType', 'Solo se permiten archivos PDF', (value: any) => {
            if (!value) return true;
            return value.type === 'application/pdf';
          })
          .test(
            'fileSize',
            'El archivo supera el límite de 5MB.',
            (value: any) => {
              if (!value) return true;
              return value.size <= MAX_SIZE;
            },
          ),

        // comments: Yup.string()
        //   .trim()
        //   .required('El comentario es requerido')
        //   .max(500, 'El comentario no puede exceder 500 caracteres'),
      }),
    [],
  );

  const formBalance = useFormik({
    validationSchema,
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values, { setStatus }) => {
      setLoading(true);

      try {
        const efectivo = Number(values.efectivo || 0);
        const virtual = Number(values.virtual || 0);

        const commissionValue = Math.max(virtual - efectivo, 0); // evita negativos

        const body = {
          // [isAdmin ? 'companyId' : 'distributorId']: values.empresa?.id,
          companyId: values.empresa?.id,

          saldoVirtualAmount: efectivo,
          commission: commissionValue,

          payType: values.tipoPago?.id,
          pdfFile: values.file,
          comments: values.comments.trim(),
          ...(isConsignment && { is_consignment: true }),
        };

        const res = await approvisionamiento(body);

        if (res?.data?.status === 'error') {
          const errorMessage =
            res?.data?.details?.[0]?.message || 'Hubo un problema con la operación';
          SimpleAlert(errorMessage, 3000, 'error');
        } else if (res?.data) {
          SimpleAlert(
            'Aprovisionamiento realizado exitosamente',
            3000,
            'success',
          );

          // UPDATE SALDO
          const user = await getUserByToken();
          dispatch(auth.actions.fulfillUser(user));

          handleClose(true);
          resetForm();
        } else {
          SimpleAlert('Hubo un problema con la operación', 3000, 'error');
        }
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.details?.[0]?.message ||
          'Error inesperado al realizar la operación';
        SimpleAlert(errorMessage, 3000, 'error');
      } finally {
        setLoading(false);
      }
    },
  });

  // Reset form helper
  const resetForm = () => {
    formBalance.resetForm();
    setCommission(0);
    setTypeCommission('');
    setRadioSelection('');
    setFileName('');
    setIsConsignment(false);
    formBalance.setFieldValue('file', null);
    setSaldoActual(0);
  };

  // Handlers
  const handleTypeClientChange = (selectedOption: SelectOption | null) => {
    if (!selectedOption) return;

    formBalance.setFieldValue('cliente', selectedOption);
    formBalance.setFieldValue('empresa', null);
    setCommission(0);
    setTypeCommission('');
    setSaldoActual(0);

    const clientType = selectedOption.value;
    if (clientType === 'socio') {
      setRadioSelection('SOC');
    } else if (clientType === 'empresa') {
      setRadioSelection('EMP');
    } else if (clientType === 'distribuidor') {
      setRadioSelection('DIST');
    }
  };

  const resetFile = () => {
    if (inputRef.current) inputRef.current.value = '';
    formBalance.setFieldValue('file', null);
    setFileName('');
    setFileSize(0);
  };

  const validateAndSetPdf = (file: File) => {
    if (file.type !== 'application/pdf') {
      SimpleAlert('El archivo debe ser un PDF.', 3000, 'error');
      resetFile();
      return;
    }

    if (file.size > MAX_SIZE) {
      SimpleAlert('El archivo supera el límite de 5MB.', 3000, 'error');
      resetFile();
      return;
    }

    formBalance.setFieldValue('file', file);
    setFileName(file.name);
    setFileSize(file.size);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    validateAndSetPdf(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    validateAndSetPdf(file);
  };

  const handleDistribuidorChange = (value: SelectOption | null) => {
    if (value?.account) {
      const commissionType = value.account.typeCommission.code;
      setTypeCommission(commissionType);
      // Si es efectivo (EFF), la comisión es 0%, si es Saldo Virtual (SVV), usar el porcentaje
      setCommission(
        // commissionType === 'EFF'
        //   ? 0
        //   :
        Number(value.account.commission_percent) / 100,
      );
      formBalance.setFieldValue('virtual', 0);
      formBalance.setFieldValue('efectivo', 0);
    }
    formBalance.setFieldValue('empresa', value);
    setSaldoActual(value?.saldo || 0);
  };

  const handleEmpresaChange = (value: SelectOption | null) => {
    if (value?.account) {
      const commissionType = value.account.typeCommission.code;
      setTypeCommission(commissionType);
      // Si es efectivo (EFF), la comisión es 0%, si es Saldo Virtual (SVV), usar el porcentaje
      setCommission(
        // commissionType === 'EFF'
        //   ? 0
        //   :
        Number(value.account.commission_percent) / 100,
      );
      formBalance.setFieldValue('virtual', 0);
      formBalance.setFieldValue('efectivo', 0);
      formBalance.setFieldValue('commission', 0);
    }
    formBalance.setFieldValue('empresa', value);

    setSaldoActual(value?.saldo || 0);
  };

  const handleCloseModal = () => {
    setTimeout(() => {
      resetForm();
    }, 300);
    handleClose(false);
  };

  const onEffectiveChange = (values: { floatValue?: number }) => {
    const efectivo = values.floatValue ?? 0;

    formBalance.setFieldValue('efectivo', efectivo);
    const commissionValue = efectivo * commission;
    const virtualCalculado =
      typeCommission === 'EFF'
        ? efectivo
        : efectivo
          ? efectivo * (1 + commission)
          : 0;
    formBalance.setFieldValue('commission', commissionValue);

    formBalance.setFieldValue('virtual', virtualCalculado);
  };

  const onVirtualChange = (values: { floatValue?: number }) => {
    const { floatValue } = values;
    formBalance.setFieldValue('virtual', floatValue ?? 0);
    formBalance.setFieldValue(
      'efectivo',
      floatValue ? floatValue * (1 - commission) : 0,
    );
  };

  const fetchInitialData = async () => {
    try {
      const [tiposPagoData, distribuidoresData] = await Promise.all([
        getSelectCatalog('TDP'),
        getAllSelect({}),
      ]);

      setTiposPago(tiposPagoData || []);

      const distribuidoresActivos = (distribuidoresData || []).filter(
        (distribuidor: SelectOption) => distribuidor.active === true
      );

      setDistribuidores(distribuidoresActivos);

      if (isAdmin) {
        const empresasData = await getSelectEmpresas();
        setEmpresas(empresasData || []);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      SimpleAlert('Error al cargar los datos iniciales', 3000, 'error');
    }
  };

  useEffect(() => {
    if (!show) return;

    resetForm();
    fetchInitialData();
  }, [show, isAdmin]);

  // Error message helper
  const ErrorMessage: React.FC<{ name: keyof ApprovisionValues }> = ({
    name,
  }) => {
    const touched = formBalance.touched[name];
    const error = formBalance.errors[name];

    if (!touched || !error) return null;

    return (
      <div className="fv-plugins-message-container text-danger w-auto">
        <span role="alert">{error as string}</span>
      </div>
    );
  };

  const nuevoSaldo = useMemo(() => {
    const efectivo = Number(formBalance.values.efectivo || 0);
    const virtual = Number(formBalance.values.virtual || 0);
    const base = Number(saldoActual || 0);

    const abono = typeCommission === 'SVV' ? virtual : efectivo; // el campo activo
    return base + abono;
  }, [
    saldoActual,
    typeCommission,
    formBalance.values.efectivo,
    formBalance.values.virtual,
  ]);
  const comisionOtorgada = useMemo(() => {
    if (typeCommission === 'EFF') {
      return Number(formBalance.values.commission || 0);
    }

    const efectivo = Number(formBalance.values.efectivo || 0);
    const virtual = Number(formBalance.values.virtual || 0);

    return Math.max(virtual - efectivo, 0);
  }, [
    typeCommission,
    formBalance.values.efectivo,
    formBalance.values.virtual,
    formBalance.values.commission,
  ]);

  return (
    <>
      <Modal
        id="kt_modal_create"
        tabIndex={-1}
        aria-hidden="true"
        dialogClassName="modal-dialog modal-dialog-centered modal-xl"
        show={show}
        onHide={handleCloseModal}
      >
        <div className="modal-content">
          <div className="modal-header pb-0 border-0 justify-content-end">
            <div
              className="btn btn-sm btn-icon btn-active-color-primary"
              data-bs-dismiss="modal"
              onClick={handleCloseModal}
            >
              <KTSVG
                className="svg-icon-2"
                path="/media/icons/duotune/arrows/arr061.svg"
              />
            </div>
          </div>
          <div className="modal-body scroll-y mx-5 pt-0">
            <div className="text-center mb-5">
              <h1 className="text-center mb-13">Aprovisionar Saldo</h1>
              <div className="text-muted fw-semibold fs-5 lh-sm mx-auto w-75">
                Completa la información para aprovisionar saldo ala cuenta del
                cliente. el saldo virtual se calculará automáticamente según el
                monto ingresado.
              </div>
            </div>
            <div className="modal-body scroll-y mx-5 mx-xl-15 my-7">
              <form className="form" onSubmit={formBalance.handleSubmit}>
                {/* Sección 1: Selección de Cliente */}
                <div className="mb-10">
                  <div className="text-gray-700 mt-7 fs-4 fw-bold">
                    Datos de identificación
                  </div>
                  <div className="separator my-5"></div>
                  <div className="row g-5 mb-8">
                    {isAdmin ? (
                      <>
                        <div className="col-md-4">
                          <label className="required form-label fw-bold fs-6">
                            Tipo de cliente
                          </label>
                          <Select
                            options={typeClient}
                            styles={styleSelect}
                            menuPosition="fixed"
                            placeholder="Selecciona tipo de cliente"
                            isSearchable
                            noOptionsMessage={() =>
                              'No se encontraron registros'
                            }
                            onChange={handleTypeClientChange}
                            value={formBalance.values.cliente}
                            backspaceRemovesValue
                          />
                          <ErrorMessage name="cliente" />
                        </div>
                        <div className="col-md-4">
                          <label className="required form-label fw-bold fs-6">
                            {radioSelection === 'DIST' ? 'Distribuidor' : 'Cliente'}
                          </label>
                          {radioSelection === 'DIST' ? (
                            <Select
                              options={distribuidoresAdminOptions}
                              styles={styleSelect}
                              menuPosition="fixed"
                              placeholder="Selecciona distribuidor"
                              isSearchable
                              noOptionsMessage={() =>
                                'No se encontraron registros'
                              }
                              onChange={handleDistribuidorChange}
                              value={formBalance.values.empresa}
                              backspaceRemovesValue
                            />
                          ) : (
                            <Select
                              options={empresasOptions}
                              styles={styleSelect}
                              menuPosition="fixed"
                              placeholder="Selecciona cliente"
                              isSearchable
                              isDisabled={!radioSelection}
                              noOptionsMessage={() =>
                                'No se encontraron registros'
                              }
                              onChange={handleEmpresaChange}
                              value={formBalance.values.empresa}
                              backspaceRemovesValue
                            />
                          )}
                          <ErrorMessage name="empresa" />
                        </div>
                      </>
                    ) : (
                      <div className="col-md-4">
                        <label className="required form-label fw-bold fs-6">
                          Distribuidor
                        </label>
                        <Select
                          options={distribuidores}
                          styles={styleSelect}
                          menuPosition="fixed"
                          placeholder="Selecciona distribuidor"
                          isSearchable
                          noOptionsMessage={() => 'No se encontraron registros'}
                          onChange={handleDistribuidorChange}
                          value={formBalance.values.empresa}
                          backspaceRemovesValue
                        />
                        <ErrorMessage name="empresa" />
                      </div>
                    )}
                    <div className="col-md-2">
                      <div className="w-100 d-flex">
                        <label className="form-label fw-bold fs-6">
                          Tipo de comisión
                        </label>
                      </div>
                      <input
                        className="form-control mb-3 mb-lg-0"
                        type="text"
                        disabled
                        value={
                          typeCommission === 'EFF'
                            ? 'Efectivo'
                            : typeCommission === 'SVV'
                              ? 'Virtual'
                              : ''
                        }
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="required form-label fw-bold fs-6">
                        % Comision
                      </label>
                      <NumericFormat
                        name="commission"
                        className="form-control"
                        value={commission * 100}
                        decimalScale={2}
                        fixedDecimalScale
                        placeholder="0.00 %"
                        suffix=" %"
                        thousandSeparator=","
                        disabled
                      />
                    </div>
                  </div>
                </div>

                {/* Sección 2: Montos */}
                {/* {commission > 0 && ( */}
                <div className="row g-5 mb-3 align-items-end">
                  {/* Monto */}
                  <div className="text-gray-700 mt-7 fs-4 fw-bold">
                    Conversión de saldo
                  </div>
                  <div className="separator my-5"></div>
                  <div className="col-md-4">
                    <label className="required form-label fw-bold fs-6">
                      Monto solicitado
                    </label>
                    <NumericFormat
                      name="efectivo"
                      className="form-control"
                      value={formBalance.values.efectivo}
                      decimalScale={2}
                      decimalSeparator="."
                      fixedDecimalScale
                      placeholder="$ 0.00"
                      prefix="$ "
                      maxLength={16}
                      min={0}
                      allowNegative={false}
                      thousandSeparator=","
                      // disabled={typeCommission !== 'SVV'}
                      onValueChange={onEffectiveChange}
                    />
                  </div>

                  {/* Icono */}
                  {/* <div className="col-md-1 d-flex justify-content-center">
                    <div
                      className="d-flex align-items-center"
                      style={{ height: '38px' }}
                    >
                      <KTSVG
                        path="/media/icons/duotune/general/gen062.svg"
                        className="svg-icon-2 text-gray-500"
                      />
                    </div>
                  </div> */}

                  {/* Saldo Virtual */}
                  <div className="col-md-4">
                    <label className="form-label fw-bold fs-6">
                      Comisión otorgada
                    </label>
                    <NumericFormat
                      name="virtual"
                      className="form-control"
                      value={comisionOtorgada}
                      decimalScale={2}
                      decimalSeparator="."
                      fixedDecimalScale
                      placeholder="$ 0.00"
                      prefix="$ "
                      maxLength={16}
                      thousandSeparator=","
                      disabled
                    // ={typeCommission !== 'EFF'}
                    />
                  </div>

                  {/* Icono */}
                  {/* <div className="col-md-1 d-flex justify-content-center">
                    <div
                      className="d-flex align-items-center"
                      style={{ height: '38px' }}
                    >
                      <KTSVG
                        path="/media/icons/duotune/general/gen060.svg"
                        className="svg-icon-2 text-gray-500"
                      />
                    </div>
                  </div> */}

                  {/* Nuevo saldo */}
                  <div className="col-md-4">
                    <label className="form-label fw-bold fs-6">
                      Saldo a aprovisionar
                    </label>
                    <NumericFormat
                      name="nuevo_saldo"
                      className="form-control"
                      value={formBalance.values.virtual}
                      decimalScale={2}
                      decimalSeparator="."
                      fixedDecimalScale
                      placeholder="$ 0.00"
                      prefix="$ "
                      maxLength={16}
                      thousandSeparator=","
                      disabled
                      onValueChange={onVirtualChange}
                    />
                  </div>
                </div>
                <div className="row g-5 mb-8 pt-5">
                  {/* Saldo anterior */}
                  <div className="col-md-6">
                    <div className="border rounded-3 p-6 text-center h-100">
                      <div className="text-gray-500 fw-semibold fs-6 mb-2">
                        Saldo anterior
                      </div>
                      <div className="fw-bold fs-2 text-gray-700">
                        <NumericFormat
                          value={saldoActual}
                          displayType="text"
                          thousandSeparator=","
                          decimalScale={2}
                          fixedDecimalScale
                          prefix="$ "
                        />
                      </div>
                    </div>
                  </div>

                  {/* Nuevo saldo */}
                  <div className="col-md-6">
                    <div className="border rounded-3 p-6 text-center h-100">
                      <div className="text-gray-500 fw-semibold fs-6 mb-2">
                        Nuevo saldo
                      </div>
                      <div className="fw-bold fs-2 text-primary">
                        <NumericFormat
                          value={nuevoSaldo}
                          displayType="text"
                          thousandSeparator=","
                          decimalScale={2}
                          fixedDecimalScale
                          prefix="$ "
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-9">
                  <p className="text-center text-gray-600">
                    El pago de comisiones en efectivo no afecta el cálculo del
                    saldo a aprovisionar ni del nuevo saldo.
                  </p>
                </div>
                {/* )} */}

                {/* Sección 3: Información de Pago */}
                <div className="mb-10">
                  <div className="text-gray-700 mt-7 fs-4 fw-bold">
                    Información de pago
                  </div>
                  <div className="separator my-5"></div>
                  <div className="row g-5">
                    <div className="col-md-4">
                      <label className="required form-label fw-bold fs-6">
                        Tipo de Pago
                      </label>
                      <Select
                        options={tiposPago}
                        styles={styleSelect}
                        menuPosition="fixed"
                        placeholder="Selecciona tipo de pago"
                        isSearchable
                        noOptionsMessage={() => 'No se encontraron registros'}
                        onChange={(val) =>
                          formBalance.setFieldValue('tipoPago', val)
                        }
                        value={formBalance.values.tipoPago}
                        backspaceRemovesValue
                      />
                      <ErrorMessage name="tipoPago" />
                    </div>

                    <div className="col-md-12">
                      <div
                        className="border border-dashed border-gray-500 d-flex justify-content-center text-center flex-column p-8 h-250px overflow-hidden"
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={handleClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') handleClick();
                        }}
                      >
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          ref={inputRef}
                          style={{ display: 'none' }}
                        />

                        <a className="text-gray-800 text-hover-primary d-flex flex-column">
                          <div className="symbol symbol-75px mb-6">
                            <img
                              src={toAbsoluteUrl('/media/svg/files/pdf.svg')}
                              alt="PDF"
                            />
                          </div>

                          <div className="fs-5 fw-bolder mb-2">
                            {fileName
                              ? `Archivo seleccionado: ${fileName}`
                              : 'Arrastre y suelte su PDF aquí o seleccione un archivo'}
                          </div>
                        </a>

                        <div className="fs-7 fw-bold text-gray-500 mt-auto">
                          {fileName
                            ? `Peso del archivo: ${(
                              fileSize /
                              (1024 * 1024)
                            ).toFixed(2)} MB`
                            : 'Tamaño máximo de archivo: 5 MB'}
                        </div>
                      </div>

                      <ErrorMessage name="file" />
                    </div>
                  </div>
                </div>

                {/* Sección 4: Comentario */}
                {/* <div className="mb-8">
                  <label
                    htmlFor="formComment"
                    className="required form-label fw-bold fs-6"
                  >
                    Comentarios
                  </label>
                  <textarea
                    id="formComment"
                    className="form-control"
                    {...formBalance.getFieldProps('comments')}
                    rows={4}
                    style={{ resize: 'none' }}
                    maxLength={500}
                    placeholder="Escribe un comentario sobre este aprovisionamiento..."
                  />
                  <div className="text-muted fs-7 mt-1">
                    {formBalance.values.comments.length}/500 caracteres
                  </div>
                  <ErrorMessage name="comments" />
                </div> */}

                <div className="text-center pt-10">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    onClick={handleCloseModal}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        Espere por favor...
                        <span className="spinner-border spinner-border-sm align-middle ms-2" />
                      </>
                    ) : (
                      'Guardar'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
export { AprovisionamientoModal };
